import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import {
    Autocomplete,
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    Link,
    Paper,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import { Prompt } from 'react-router-dom';

import { CustomerSettingsRequest, CustomerUserDetails, UserDetailsResponse, UserRole } from '../../../backendsdk';
import ConfirmDialog from '../../../components/ConfirmDialog';
import {
    TrackedButton as Button,
    TrackedMenuItem as MenuItem,
    TrackedLink,
} from '../../../components/TrackedComponents';
import { useAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import useCustomer from '../../../hooks/customer';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import Logo from '../../../images/logo/logo.svg';
import { flattenRoles, malfunctionsPageDefs } from '../../../utils/Pages';
import palette from '../../ColorPalette';
import { DEFAULT_TITLE } from '../../Layout';
import { getUserAvatar } from '../Coaching/SessionDetails';
import { getIcon } from '../Overview/MapIcons';
import { OE_ROLES } from '../Users/UsersComponent';
import DefaultLocationModal from './DefaultLocationModal';

export const DEFAULT_IL_LOCATION = new L.LatLng(32.0821759, 34.7805383);
export const DEFAULT_US_LOCATION = new L.LatLng(40.7577561, -73.9870982);

const OrganizationSettings: React.FC = () => {
    const { profile } = useProfile();
    const [orgSettings, setOrgSettings] = useState<CustomerSettingsRequest>();
    const hasDefaultLocation = !!orgSettings?.default_lat || !!orgSettings?.default_lng;
    const initialLocation = hasDefaultLocation
        ? new L.LatLng(orgSettings.default_lat, orgSettings.default_lng)
        : profile.customer.settings.country === 'il'
        ? DEFAULT_IL_LOCATION
        : DEFAULT_US_LOCATION;
    const [defaultMapPosition, setDefaultMapPosition] = useState<L.LatLng>(initialLocation);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState<boolean>(false);
    const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
    const [isLoadingAction, setIsLoadingAction] = useState<boolean>(false);
    const [users, setUsers] = useState<UserDetailsResponse[]>([]);
    const [deactivateUsers, setDeactivateUsers] = useState<boolean>();
    const [lockUsers, setLockUsers] = useState<boolean>();
    const [isLoadingSticker, setIsLoadingSticker] = useState<boolean>(false);
    const [alertElement, setAlert] = useAlert();
    const { i18n, t } = useTranslation();
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();
    const { api } = useApi();
    const { customer, isFetching, isError } = useCustomer();
    const customerUsers = customer?.users || [];
    const userById = customerUsers.reduce((acc, user) => {
        acc[user.user_id] = user;
        return acc;
    }, {} as Record<string, CustomerUserDetails>);

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t('content.org_settings.header')}`;
    }, []);

    useEffect(() => {
        api.apiV0SettingsUserManagementGet().then((res) => setUsers(res.data));
    }, []);

    useEffect(() => {
        if (customer) {
            setOrgSettings({
                name: customer.display_name,
                logo: customer.logo_url,
                use_speeding_policy: customer.settings.use_speeding_policy,
                speeding_policy_threshold: customer.settings.speeding_policy_threshold,
                otp_enforced: customer.settings.otp_enforced,
                use_auto_logout: customer.settings.use_auto_logout,
                auto_logout_after_minutes: customer.settings.auto_logout_after_minutes,
                deactivate_users_after_days: customer.settings.deactivate_users_after_days,
                lock_user_after_failed_attempts: customer.settings.lock_user_after_failed_attempts,
                default_lat: customer.settings.default_lat,
                default_lng: customer.settings.default_lng,
                default_malfunction_assignee: customer.settings.default_malfunction_assignee,
                remind_driver_to_complete_session: customer.settings.remind_driver_to_complete_session,
                remind_driver_after_days: customer.settings.remind_driver_after_days,
                driver_reminder_limit: customer.settings.driver_reminder_limit,
            });
            setDeactivateUsers(!!customer.settings.deactivate_users_after_days);
            setLockUsers(!!customer.settings.lock_user_after_failed_attempts);
        }
    }, [customer]);

    useEffect(() => {
        if (orgSettings?.default_lat || orgSettings?.default_lng) {
            setDefaultMapPosition(new L.LatLng(orgSettings.default_lat, orgSettings.default_lng));
        }
    }, [orgSettings?.default_lat, orgSettings?.default_lng]);

    const detailsChanged =
        !!customer &&
        !isFetching &&
        !!orgSettings &&
        (orgSettings.name !== customer.display_name ||
            orgSettings.logo !== customer.logo_url ||
            orgSettings.otp_enforced !== customer.settings.otp_enforced ||
            orgSettings.use_auto_logout !== customer.settings.use_auto_logout ||
            (orgSettings.use_auto_logout &&
                orgSettings.use_auto_logout === customer.settings.use_auto_logout &&
                orgSettings.auto_logout_after_minutes !== customer.settings.auto_logout_after_minutes) ||
            orgSettings.default_lat !== customer.settings.default_lat ||
            orgSettings.default_lng !== customer.settings.default_lng ||
            orgSettings.deactivate_users_after_days !== customer.settings.deactivate_users_after_days ||
            orgSettings.lock_user_after_failed_attempts !== customer.settings.lock_user_after_failed_attempts ||
            orgSettings.default_malfunction_assignee !== customer.settings.default_malfunction_assignee ||
            orgSettings.remind_driver_to_complete_session !== customer.settings.remind_driver_to_complete_session ||
            orgSettings.remind_driver_after_days !== customer.settings.remind_driver_after_days ||
            orgSettings.driver_reminder_limit !== customer.settings.driver_reminder_limit);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onload = (e) => {
                setOrgSettings((prev) => {
                    if (!prev) {
                        return prev;
                    }
                    return { ...prev, logo: e.target?.result as string };
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = () => {
        setIsLoadingAction(true);
        api.apiV0SettingsCustomerPatch({ customerSettingsRequest: orgSettings })
            .then(() => {
                queryClient.invalidateQueries(['customer']);
                setAlert({
                    message: t('content.org_settings.save_success'),
                    type: 'success',
                    duration: 6000,
                });
                setIsLoadingAction(false);
            })
            .catch(() =>
                setAlert({
                    message: t('content.org_settings.save_error'),
                    type: 'error',
                    duration: 6000,
                }),
            );
    };

    const getSticker = () => {
        setIsLoadingSticker(true);
        api.apiV0SettingsCustomerStickerPost({ responseType: 'blob' })
            .then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'sticker.pdf');
                document.body.appendChild(link);
                link.click();
            })
            .catch(() =>
                setAlert({ message: t('content.org_settings.create_sticker_error'), type: 'error', duration: 6000 }),
            )
            .finally(() => setIsLoadingSticker(false));
    };

    if (!orgSettings || isError) {
        return (
            <Box
                sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                {isError ? (
                    <Typography variant="overline" color="error">
                        {t('content.org_settings.error_loading')}
                    </Typography>
                ) : (
                    <CircularProgress />
                )}
            </Box>
        );
    }

    const isDeactivateUsersValid =
        orgSettings.deactivate_users_after_days === null || orgSettings.deactivate_users_after_days > 0;
    const isLockUsersValid =
        orgSettings.lock_user_after_failed_attempts === null || orgSettings.lock_user_after_failed_attempts > 0;
    const isRemindDriversValid =
        orgSettings.remind_driver_after_days === null || orgSettings.remind_driver_after_days > 0;
    const isReminderLimitValid = orgSettings.driver_reminder_limit === null || orgSettings.driver_reminder_limit > 0;

    const saveButton = (
        <Button
            id="save-btn"
            data-testid="save-btn"
            disabled={
                !detailsChanged ||
                !orgSettings.name ||
                (orgSettings.use_auto_logout && !orgSettings.auto_logout_after_minutes) ||
                (deactivateUsers && !orgSettings.deactivate_users_after_days) ||
                (lockUsers && !orgSettings.lock_user_after_failed_attempts) ||
                !isDeactivateUsersValid ||
                !isLockUsersValid ||
                !isRemindDriversValid ||
                !isReminderLimitValid
            }
            startIcon={<SaveIcon />}
            variant="contained"
            color="secondary"
            onClick={() => {
                if (
                    !customer?.settings.otp_enforced &&
                    orgSettings.otp_enforced &&
                    users.filter((user) => !OE_ROLES.includes(user.role as UserRole) && !user.phone_number).length > 0
                ) {
                    setIsWarningModalOpen(true);
                } else {
                    handleSave();
                }
            }}
        >
            {t('content.org_settings.save')}
        </Button>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                overflowY: 'auto',
                p: 2,
                pt: 0,
            }}
        >
            <Prompt when={detailsChanged} message={t('content.accidents.prompt_msg')} />
            {alertElement}
            {isLocationModalOpen ? (
                <DefaultLocationModal
                    open={isLocationModalOpen}
                    onClose={() => setIsLocationModalOpen(false)}
                    position={defaultMapPosition}
                    setPosition={(point: L.LatLng) => {
                        setOrgSettings((prev) => {
                            if (!prev) {
                                return prev;
                            }
                            return { ...prev, default_lat: point.lat, default_lng: point.lng };
                        });
                    }}
                    isMarkerVisible={hasDefaultLocation}
                />
            ) : null}
            {isWarningModalOpen ? (
                <ConfirmDialog
                    isDialogOpen={isWarningModalOpen}
                    isLoading={isLoadingAction}
                    onClose={() => setIsWarningModalOpen(false)}
                    headerText={t('content.org_settings.warning_header')}
                    bodyText={[t('content.org_settings.warning_body_1'), t('content.org_settings.warning_body_2')]}
                    buttonText={t('content.org_settings.continue')}
                    buttonColor="secondary"
                    onConfirm={() => {
                        handleSave();
                        setIsWarningModalOpen(false);
                    }}
                />
            ) : null}
            <Box sx={{ position: 'sticky', top: 0, backgroundColor: palette.bgColor, zIndex: 1002, pt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h5">{t('content.org_settings.header')}</Typography>
                        <Typography variant="body2" sx={{ color: palette.neutral[700] }}>
                            {t('content.org_settings.subheader')}
                        </Typography>
                    </Box>
                    {!isMobile ? saveButton : null}
                </Box>
                <Divider />
            </Box>
            <Grid container columns={3} sx={{ maxWidth: 700 }} padding={2}>
                <Grid item xs={3} sm={1}>
                    <Typography
                        variant="body2"
                        sx={{ color: palette.neutral[700], fontWeight: 'bold', mb: isMobile ? 2 : 0 }}
                    >
                        {t('content.org_settings.info.header')}
                    </Typography>
                </Grid>
                <Grid item xs={3} sm={2}>
                    <TextField
                        id="org-name-input"
                        value={orgSettings.name}
                        onChange={(e) =>
                            setOrgSettings((prev) => {
                                if (!prev) {
                                    return prev;
                                }
                                return { ...prev, name: e.target.value };
                            })
                        }
                        error={!orgSettings.name}
                        fullWidth
                        label={t('content.org_settings.info.name')}
                        size="small"
                        inputProps={{ 'data-testid': 'username-input' }}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: palette.neutral[700], fontWeight: 'bold' }}>
                                {t('content.org_settings.info.logo')}
                            </Typography>
                            <Tooltip title={t(`content.org_settings.info.logo_tooltip`)}>
                                <InfoOutlinedIcon sx={{ fontSize: 16, ml: 0.25, color: palette.neutral[700] }} />
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '233px',
                                my: 1,
                                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                                position: 'relative',
                            }}
                        >
                            <Paper
                                component="label"
                                sx={{
                                    position: 'absolute',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    top: 0,
                                    left: 0,
                                    opacity: 0,
                                    zIndex: 2,
                                    width: '100%',
                                    height: '100%',
                                    '&:hover': { opacity: 1 },
                                    background:
                                        'linear-gradient(180deg, rgba(101, 101, 101, 0.21) 0%, rgba(0, 0, 0, 0.6) 100%)',
                                    transition: 'opacity 0.2s',
                                    cursor: 'pointer',
                                    borderRadius: 0,
                                }}
                            >
                                <EditIcon sx={{ color: palette.white }} />
                                <input
                                    id="image-field"
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                            </Paper>
                            <img
                                src={orgSettings.logo || Logo}
                                alt="logo"
                                style={{ width: '100%', display: 'block' }}
                            />
                        </Box>
                        {!!orgSettings.logo ? (
                            <TrackedLink
                                id="reset-logo-link"
                                component="button"
                                onClick={() =>
                                    setOrgSettings((prev) => {
                                        if (!prev) {
                                            return prev;
                                        }
                                        return { ...prev, logo: null };
                                    })
                                }
                            >
                                <Typography fontSize={12} sx={{ color: '#5B5B5B' }}>
                                    {t('content.org_settings.info.reset')}
                                </Typography>
                            </TrackedLink>
                        ) : null}
                        <Box sx={{ mt: 1 }}>
                            <Button
                                id="sticker-btn"
                                data-testid="sticker-btn"
                                disabled={isLoadingSticker}
                                startIcon={
                                    isLoadingSticker ? (
                                        <CircularProgress size={18} thickness={5} />
                                    ) : (
                                        <StickyNote2Icon />
                                    )
                                }
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    getSticker();
                                }}
                            >
                                {t('content.org_settings.create_sticker')}
                            </Button>
                            <Tooltip title={t('content.org_settings.create_sticker_tooltip')}>
                                <InfoOutlinedIcon sx={{ fontSize: 16, ml: 0.5, mb: 0.5 }} />
                            </Tooltip>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <Divider />
            <Grid container columns={4} sx={{ maxWidth: 920 }} padding={2}>
                <Grid item xs={4} sm={1}>
                    <Typography
                        variant="body2"
                        sx={{ color: palette.neutral[700], fontWeight: 'bold', mb: isMobile ? 2 : 0 }}
                    >
                        {t('content.org_settings.policies.header')}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={3}>
                    <Grid container columns={3} rowSpacing={2} columnSpacing={1.5}>
                        <Grid item xs={3} sm={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="enforce-speed-policy-checkbox"
                                            data-testid="enforce-speed-policy-checkbox"
                                            disabled
                                            checked={orgSettings.use_speeding_policy}
                                            size="small"
                                            disableRipple
                                            sx={{
                                                '& .MuiSvgIcon-root': {
                                                    height: 18,
                                                    width: 18,
                                                    marginBottom: '2px',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            {t('content.org_settings.policies.enforce_speeding_policy')}
                                        </Typography>
                                    }
                                    sx={{ mb: 0.5, mr: 0.25 }}
                                />
                                <Tooltip title={t('content.org_settings.policies.enforce_speeding_policy_tooltip')}>
                                    <InfoOutlinedIcon sx={{ fontSize: 16, ml: 0.25, mb: 0.5 }} />
                                </Tooltip>
                            </Box>
                            <TextField
                                id="max-speed-input"
                                label={t(
                                    `content.org_settings.policies.max_speed_${
                                        profile.customer.settings.use_imperial_units ? 'mph' : 'kmh'
                                    }`,
                                )}
                                disabled
                                value={orgSettings.speeding_policy_threshold}
                                size="small"
                                sx={{ maxWidth: '250px' }}
                            />
                        </Grid>
                        <Grid item xs={3} sm={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Autocomplete
                                fullWidth
                                id="assignee-field"
                                data-testid="assignee-field"
                                size="small"
                                value={orgSettings.default_malfunction_assignee}
                                onChange={(_event, newValue) => {
                                    setOrgSettings((prev) => {
                                        if (!prev) {
                                            return prev;
                                        }
                                        return { ...prev, default_malfunction_assignee: newValue };
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputProps={{ ...params.inputProps, 'data-testid': 'assignee-field-input' }}
                                        label={t('content.org_settings.policies.default_malfunction_assignee')}
                                        fullWidth
                                    />
                                )}
                                getOptionLabel={(option) => userById[option]?.name || ''}
                                renderOption={(props, option) => {
                                    const user = userById[option];
                                    return (
                                        <MenuItem
                                            id={`user-${user.user_id}-menu-item`}
                                            data-testid={`user-${user.user_id}-menu-item`}
                                            {...props}
                                            key={user.user_id}
                                        >
                                            {getUserAvatar(user)}
                                            <Typography variant="body2">{user.name}</Typography>
                                        </MenuItem>
                                    );
                                }}
                                options={customerUsers
                                    .filter((u) => flattenRoles(malfunctionsPageDefs).includes(u.role))
                                    .map((u) => u.user_id)}
                                sx={{ maxWidth: '250px' }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
                                <FormControlLabel
                                    id="remind-drivers-field"
                                    data-testid="remind-drivers-field"
                                    control={
                                        <Checkbox
                                            id="remind-drivers-checkbox"
                                            checked={orgSettings.remind_driver_to_complete_session}
                                            onChange={(e) => {
                                                setOrgSettings((prev) => {
                                                    if (!prev) {
                                                        return prev;
                                                    }
                                                    return {
                                                        ...prev,
                                                        remind_driver_to_complete_session: e.target.checked,
                                                        remind_driver_after_days: e.target.checked ? 1 : null,
                                                        driver_reminder_limit: e.target.checked ? 2 : null,
                                                    };
                                                });
                                            }}
                                            size="small"
                                            disableRipple
                                            sx={{
                                                '& .MuiSvgIcon-root': {
                                                    height: 18,
                                                    width: 18,
                                                    marginBottom: '2px',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                                            {t('content.org_settings.policies.remind_drivers_1')}
                                        </Typography>
                                    }
                                    sx={{ mr: 0 }}
                                />
                                <TextField
                                    id="remind-drivers-days"
                                    data-testid="remind-drivers-days"
                                    type="number"
                                    size="small"
                                    disabled={!orgSettings.remind_driver_to_complete_session}
                                    value={orgSettings.remind_driver_after_days || ''}
                                    onChange={(e) =>
                                        setOrgSettings((prev) => {
                                            if (!prev) {
                                                return prev;
                                            }
                                            return {
                                                ...prev,
                                                remind_driver_after_days:
                                                    e.target.value === '' ? null : parseInt(e.target.value),
                                            };
                                        })
                                    }
                                    error={
                                        orgSettings.remind_driver_to_complete_session &&
                                        !orgSettings.remind_driver_after_days
                                    }
                                    sx={{
                                        width: 100,
                                        flexShrink: 0,
                                        ml: i18n.languages[0] === 'he' ? 0 : 0.5,
                                        mr: 0.5,
                                    }}
                                />
                                <Typography fontSize={14} sx={{ whiteSpace: 'nowrap' }}>
                                    {t('content.org_settings.policies.remind_drivers_2')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5, pl: '25px' }}>
                                <Typography fontSize={14} sx={{ whiteSpace: 'nowrap' }}>
                                    {t('content.org_settings.policies.remind_drivers_3')}
                                </Typography>
                                <TextField
                                    id="driver-reminder-limit"
                                    data-testid="driver-reminder-limit"
                                    type="number"
                                    size="small"
                                    disabled={!orgSettings.remind_driver_to_complete_session}
                                    value={orgSettings.driver_reminder_limit || ''}
                                    onChange={(e) =>
                                        setOrgSettings((prev) => {
                                            if (!prev) {
                                                return prev;
                                            }
                                            return {
                                                ...prev,
                                                driver_reminder_limit:
                                                    e.target.value === '' ? null : parseInt(e.target.value),
                                            };
                                        })
                                    }
                                    error={
                                        orgSettings.remind_driver_to_complete_session &&
                                        !orgSettings.driver_reminder_limit
                                    }
                                    sx={{
                                        width: 100,
                                        flexShrink: 0,
                                        ml: i18n.languages[0] === 'he' ? 0 : 0.5,
                                        mr: 0.5,
                                    }}
                                />
                                <Typography fontSize={14} sx={{ whiteSpace: 'nowrap' }}>
                                    {t('content.org_settings.policies.remind_drivers_4')}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Divider />
            <Grid container columns={4} sx={{ maxWidth: 920 }} padding={2}>
                <Grid item xs={4} sm={1}>
                    <Typography
                        variant="body2"
                        sx={{ color: palette.neutral[700], fontWeight: 'bold', mb: isMobile ? 2 : 0 }}
                    >
                        {t('content.org_settings.security.header')}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                            id="use-2fa-field"
                            data-testid="use-2fa-field"
                            control={
                                <Checkbox
                                    id="use-2fa-checkbox"
                                    size="small"
                                    disableRipple
                                    checked={orgSettings.otp_enforced}
                                    onChange={(e) =>
                                        setOrgSettings((prev) => {
                                            if (!prev) {
                                                return prev;
                                            }
                                            return { ...prev, otp_enforced: e.target.checked };
                                        })
                                    }
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            height: 18,
                                            width: 18,
                                            marginBottom: '2px',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                                    {t('content.org_settings.security.two_factor_auth')}
                                </Typography>
                            }
                            sx={{ mr: 0.25 }}
                        />
                        <Tooltip title={t('content.org_settings.security.two_factor_auth_tooltip')}>
                            <InfoOutlinedIcon sx={{ fontSize: 16, ml: 0.25 }} />
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControlLabel
                            id="auto-sign-out-field"
                            data-testid="auto-sign-out-field"
                            value="bottom_drivers"
                            control={
                                <Checkbox
                                    id="auto-sign-out-checkbox"
                                    checked={orgSettings.use_auto_logout}
                                    onChange={(e) =>
                                        setOrgSettings((prev) => {
                                            if (!prev) {
                                                return prev;
                                            }
                                            return { ...prev, use_auto_logout: e.target.checked };
                                        })
                                    }
                                    size="small"
                                    disableRipple
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            height: 18,
                                            width: 18,
                                            marginBottom: '2px',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                                    {t('content.org_settings.security.auto_sign_out_1')}
                                </Typography>
                            }
                            sx={{ mr: 0 }}
                        />
                        <TextField
                            id="auto-sign-out-minutes"
                            data-testid="auto-sign-out-minutes"
                            type="number"
                            size="small"
                            disabled={!orgSettings.use_auto_logout}
                            value={orgSettings.auto_logout_after_minutes || ''}
                            onChange={(e) =>
                                setOrgSettings((prev) => {
                                    if (!prev) {
                                        return prev;
                                    }
                                    return {
                                        ...prev,
                                        auto_logout_after_minutes:
                                            e.target.value === '' ? null : parseInt(e.target.value),
                                    };
                                })
                            }
                            error={orgSettings.use_auto_logout && !orgSettings.auto_logout_after_minutes}
                            sx={{ width: 100, flexShrink: 0, ml: i18n.languages[0] === 'he' ? 0 : 0.5, mr: 0.5 }}
                        />
                        <Typography fontSize={14} sx={{ whiteSpace: 'nowrap' }}>
                            {t('content.org_settings.security.auto_sign_out_2')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', my: 0.5 }}>
                        <FormControlLabel
                            id="deactivate-users-field"
                            data-testid="deactivate-users-field"
                            control={
                                <Checkbox
                                    id="deactivate-users-checkbox"
                                    checked={deactivateUsers}
                                    onChange={(e) => {
                                        if (!e.target.checked) {
                                            setOrgSettings((prev) => {
                                                if (!prev) {
                                                    return prev;
                                                }
                                                return { ...prev, deactivate_users_after_days: null };
                                            });
                                        }
                                        setDeactivateUsers(e.target.checked);
                                    }}
                                    size="small"
                                    disableRipple
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            height: 18,
                                            width: 18,
                                            marginBottom: '2px',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                                    {t('content.org_settings.security.deactivate_users_1')}
                                </Typography>
                            }
                            sx={{ mr: 0 }}
                        />
                        <TextField
                            id="deactivate-users-days"
                            data-testid="deactivate-users-days"
                            type="number"
                            size="small"
                            disabled={!deactivateUsers}
                            value={orgSettings.deactivate_users_after_days || ''}
                            onChange={(e) =>
                                setOrgSettings((prev) => {
                                    if (!prev) {
                                        return prev;
                                    }
                                    return {
                                        ...prev,
                                        deactivate_users_after_days:
                                            e.target.value === '' ? null : parseInt(e.target.value),
                                    };
                                })
                            }
                            error={deactivateUsers && !orgSettings.deactivate_users_after_days}
                            sx={{ width: 100, flexShrink: 0, ml: i18n.languages[0] === 'he' ? 0 : 0.5, mr: 0.5 }}
                        />
                        <Typography fontSize={14} sx={{ whiteSpace: 'nowrap' }}>
                            {t('content.org_settings.security.deactivate_users_2')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControlLabel
                            id="lock-users-field"
                            data-testid="lock-users-field"
                            control={
                                <Checkbox
                                    id="lock-users-checkbox"
                                    checked={lockUsers}
                                    onChange={(e) => {
                                        if (!e.target.checked) {
                                            setOrgSettings((prev) => {
                                                if (!prev) {
                                                    return prev;
                                                }
                                                return { ...prev, lock_user_after_failed_attempts: null };
                                            });
                                        }
                                        setLockUsers(e.target.checked);
                                    }}
                                    size="small"
                                    disableRipple
                                    sx={{
                                        '& .MuiSvgIcon-root': {
                                            height: 18,
                                            width: 18,
                                            marginBottom: '2px',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                                    {t('content.org_settings.security.lock_users_1')}
                                </Typography>
                            }
                            sx={{ mr: 0 }}
                        />
                        <TextField
                            id="lock-users-attempts"
                            data-testid="lock-users-attempts"
                            type="number"
                            size="small"
                            disabled={!lockUsers}
                            value={orgSettings.lock_user_after_failed_attempts || ''}
                            onChange={(e) =>
                                setOrgSettings((prev) => {
                                    if (!prev) {
                                        return prev;
                                    }
                                    return {
                                        ...prev,
                                        lock_user_after_failed_attempts:
                                            e.target.value === '' ? null : parseInt(e.target.value),
                                    };
                                })
                            }
                            error={lockUsers && !orgSettings.lock_user_after_failed_attempts}
                            sx={{ width: 100, flexShrink: 0, ml: i18n.languages[0] === 'he' ? 0 : 0.5, mr: 0.5 }}
                        />
                        <Typography fontSize={14} sx={{ whiteSpace: 'nowrap' }}>
                            {t('content.org_settings.security.lock_users_2')}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
            <Divider />
            <Grid container columns={4} sx={{ maxWidth: 920 }} padding={2}>
                <Grid item xs={4} sm={1}>
                    <Typography
                        variant="body2"
                        sx={{ color: palette.neutral[700], fontWeight: 'bold', mb: isMobile ? 2 : 0 }}
                    >
                        {t('content.org_settings.customization.header')}
                    </Typography>
                </Grid>
                <Grid item xs={4} sm={3} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: palette.neutral[700], fontWeight: 'bold' }}>
                            {t('content.org_settings.customization.default_location')}
                        </Typography>
                        <Tooltip title={t('content.org_settings.customization.default_location_tooltip')}>
                            <InfoOutlinedIcon sx={{ fontSize: 16, ml: 0.25, color: palette.neutral[700] }} />
                        </Tooltip>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <Box
                            onClick={() => setIsLocationModalOpen(true)}
                            sx={{
                                width: '200px',
                                height: '200px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                flexShrink: 0,
                                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                                position: 'relative',
                                cursor: 'pointer',
                            }}
                        >
                            <MapContainer
                                center={defaultMapPosition}
                                zoom={15}
                                scrollWheelZoom={false}
                                dragging={false}
                                attributionControl={false}
                                zoomControl={false}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                <MapContentComponent position={defaultMapPosition} />
                                {hasDefaultLocation ? (
                                    <Marker
                                        icon={getIcon('', 'online', false)}
                                        position={[defaultMapPosition.lat, defaultMapPosition.lng]}
                                    />
                                ) : null}
                            </MapContainer>
                            {!hasDefaultLocation ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        top: 0,
                                        left: 0,
                                        zIndex: 1001,
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                    }}
                                >
                                    <Box sx={{ backgroundColor: alpha(palette.white, 0.8), px: 0.5 }}>
                                        <Typography variant="overline">
                                            {t('content.org_settings.customization.no_location')}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : null}
                        </Box>
                        <div className="leaflet-control-attribution leaflet-control" style={{ fontSize: 14 }}>
                            <Link href="https://leafletjs.com" title="A JavaScript library for interactive maps">
                                <svg
                                    aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="12"
                                    height="8"
                                    viewBox="0 0 12 8"
                                    className="leaflet-attribution-flag"
                                >
                                    <path fill="#4C7BE1" d="M0 0h12v4H0z"></path>
                                    <path fill="#FFD500" d="M0 4h12v3H0z"></path>
                                    <path fill="#E0BC00" d="M0 7h12v1H0z"></path>
                                </svg>{' '}
                                Leaflet
                            </Link>{' '}
                            <span aria-hidden="true">|</span> ©{' '}
                            <Link href="https://www.openstreetmap.org/copyright">OpenStreetMap</Link> contributors
                        </div>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

interface MapContentComponentProps {
    position: L.LatLng;
}

const MapContentComponent: React.FC<MapContentComponentProps> = (props: MapContentComponentProps) => {
    const map = useMap();

    useEffect(() => {
        map.setView(props.position, map.getZoom());
    }, [map, props.position]);

    return null;
};

export default OrganizationSettings;
